import {
  ChangeDetectorRef, Component, Input, OnInit,
  OnDestroy, ViewChild, Output, EventEmitter,
 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormArray, UntypedFormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import { segmentsData } from '../../../product-details/segments-data-stub';
import { populateModel } from 'utils/utils';
import { setItemDetailValidation } from 'shared/item-detail/item-detail-validation';
import { setErrors, isAssetItemShown, isPAG, createEmptyPAGAsset, isThirdPartyAsset } from 'shared/digital-assets/digital-asset-helpers';
import { Config } from 'shared/config/env.config';
import { DigitalAsset } from '../../models/digital-asset.model';
import { digitalAssetErrors } from 'shared/digital-assets/digital-asset-errors';

import { BaseSegmentComponent } from '../base/_base-segment.component';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';
import { AssigningAssetsComponent } from './assigning-assets/assigning-assets.component';

import { ProductPanelService } from '../../product-panel.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { BrandService } from 'shared/brand/brand.service';
import { SearchService } from 'shared/search/search.service';

import { PrimaryPhotoWarningModalComponent } from './primary-photo-warning-modal/primary-photo-warning-modal.component';


@Component({
  moduleId: module.id,
  selector: 'digital-assets-segment',
  templateUrl: 'digital-assets.component.html',
  styleUrls: ['digital-assets.component.scss'],
})
export class DigitalAssetsComponent extends BaseSegmentComponent implements OnInit, OnDestroy {

  @ViewChild(TableEditableComponent, {static: false})
  tableEditable: TableEditableComponent | void;

  @ViewChild(AssigningAssetsComponent, {static: false})
  assigningAssets: AssigningAssetsComponent | void;

  @ViewChild(PrimaryPhotoWarningModalComponent, {static: false})
  pfWarningModal: PrimaryPhotoWarningModalComponent | void;

  @Output() forceSave: EventEmitter<any> = new EventEmitter();

  @Output() forceCancel: EventEmitter<any> = new EventEmitter();

  headers: Array<string> = [
    'filename',
    'asset_type',
    'representation',
  ];
  actionItems: Array<any> = [
    {
      title: 'Add from Asset Manager',
      name: 'assign-assets'
    }
  ];
  digitalAssetsForm: UntypedFormGroup;
  declare data: Array<any>;
  declare itemDetailsData: any;
  // for removing row
  editableDetailsData: any;
  assigning: boolean = false;
  segmentName: string = 'assets';

  get digitalAssets(): UntypedFormArray {
    return this.digitalAssetsForm.get('digitalAssets') as UntypedFormArray;
  }

  get segmentForm(): UntypedFormGroup {
    return this.digitalAssetsForm;
  }

  get isTableEditing(): boolean {
    return this.tableEditable && this.tableEditable.isEditing;
  }

  get disabledAssign(): boolean {
    return !this.selectedItems.length;
  }

  get selectedItems(): Array<any> {
    return this.assigningAssets && this.assigningAssets.selectedItems;
  }

  get isMultiplePrimaryPhoto(): boolean {
    const selected: any = _.groupBy(this.digitalAssets.value, ['assetType', 'P04'])['true'];
    return selected && selected.length > 1;
  }

  constructor(
    protected productPanelService: ProductPanelService,
    protected route: ActivatedRoute,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected ref: ChangeDetectorRef,
    private fb: UntypedFormBuilder
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  ngOnInit() {
    this.digitalAssetsForm = this.fb.group({
      digitalAssets: this.fb.array([])
    });
    this.subscribeToItemDetail();
  }

  ngOnDestroy() {
    this.toggleRightPanel(false);
    this.editableDetailsData = null;
    super.ngOnDestroy();
  }

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    const itemDetailWithValidation = setItemDetailValidation(itemDetail);
    this.itemDetailValidation = itemDetailWithValidation.validation;
    this.setData(itemDetail.digitalAssets);
  }

  setData(digitalAssets: any = []): void {
    this.itemDetailsData = digitalAssets;
    this.editableDetailsData = digitalAssets;
    this.data = segmentsData.digitalAssets;
    this.setFormArray(this.data, this.itemDetailsData);
  }

  save(props: any = { warnMultiplePrimaryPhoto: true }): any {
    if (this.isMultiplePrimaryPhoto && _.get(props, 'warnMultiplePrimaryPhoto')) {
      // SPD2-2263
      if (this.pfWarningModal instanceof PrimaryPhotoWarningModalComponent) {
        this.pfWarningModal.show();
      }
      return;
    }
    if (this.bulkEdit) {
      return this.putDataBulk(
        this.segmentName,
        this.digitalAssets.value,
        this.prepareBulkData(this.editableDetailsData)
      );
    } else {
      return this.putData(this.itemId, this.segmentName, this.digitalAssets.value);
    }
  }

  getFilenameError(item: any, index: number): string {
    return !this.editableDetailsData[index].system_filename ? item.errorMessage : '';
  }

  getFilenameValidation(index: number): Array<any> {
    if (!this.editableDetailsData[index].system_filename) {
      return [
        {
          title: 'PIES 6.5',
          valid: false,
        }
      ];
    } else {
      return [];
    }
  }

  // handlers
  onRowsRemove(rows: any) {
    const indexes = _.map(rows, (row: any) => row.index);
    if (indexes.length) {
      _.pullAt(this.editableDetailsData, indexes);
    }
  }

  onAssetManagerBtnClick() {
    this.assigning = true;
  }

  onAssignBtnClick() {
    if (this.disabledAssign) { return; }
    // assign controls values on digital assets data
    _.each(this.selectedItems, (item: any) => {
      const formGroup = _.find(this.digitalAssets.controls, (assetGroup: UntypedFormGroup) => {
        return assetGroup.get('_id').value === item._id;
      });
      if (!formGroup) { return; }
      _.each(this.data, (fieldItem: any) => {
        if (!formGroup.get(fieldItem.field_map)) { return; }
        _.set(item, fieldItem.item_name, formGroup.get(fieldItem.field_map).value);
      });
    });
    this.editableDetailsData = _.cloneDeep(this.selectedItems);
    this.setFormArray(this.data, this.editableDetailsData);
    this.cancelAssigning();
    this.markAsDirty();
  }

  onCancelAssigningClick() {
    this.cancelAssigning();
  }

  onToggleListManager(toggle: boolean) {
    this.toggleRightPanel(toggle);
  }

  onPFWarningCancel() {
    this.forceCancel.emit();
  }

  onPFWarningSave() {
    this.forceSave.emit({warnMultiplePrimaryPhoto: false});
  }

  onAddUrlClick() {
    const emptyAsset: any = createEmptyPAGAsset(true);
    this.editableDetailsData.push(emptyAsset);
    const group = this.fb.group(populateModel(DigitalAsset, this.data, emptyAsset, this, 'pies65'));
    this.digitalAssets.push(group);
    this.markAsDirty();
    this.ref.markForCheck();
  }

  // private
  private setFormArray(_segmentsData: Array<any>, data: any) {
    const groups = data.map((item: any) => {
      const group = populateModel(DigitalAsset, _segmentsData, item, this, 'pies65');
      return this.fb.group(group);
    });
    setErrors(groups, this.data, this.itemDetailValidation);
    const formArray = this.fb.array(groups);
    this.digitalAssetsForm.setControl('digitalAssets', formArray);
  }

  private getFileIconLink(asset: any): any {
    return asset.thumbnail;
  }

  private isFormControl(itemName: string, i: number): boolean {
    if (this.isPAG(this.editableDetailsData[i])) {
      return itemName === 'filename' || itemName === 'file_path';
    } else {
      return itemName === 'asset_type' || itemName === 'representation';
    }
  }

  private cancelAssigning(): void {
    this.assigning = false;
    this.toggleRightPanel(false);
  }

  private toggleRightPanel(toggle: boolean): void {
    this.productPanelService.uiModel.rightPanelMoved = toggle;
  }

  private isFieldShown(item: any, data: any): boolean {
    if (this.isPAG(data)) {
      return _.get(item, 'is_pag');
    } else {
      return isAssetItemShown(data, item);
    }
  }

  private prepareBulkData(data: any): any {
    if (!data) { return []; }
    return _.map(data, (item, index) => {
      const prepared = _.assign({}, item, _.mapKeys(this.digitalAssets.value[index], (value: any, key: string) => {
        return this.camelCaseToDash(key);
      }));
      // remove pag asset fake id
      if (_.startsWith(prepared._id, 'padbID_')) { _.unset(prepared, '_id'); }
      return prepared;
    });
  }

  private hasSystemFile(asset: any): boolean {
    return !!asset.systemFilename;
  }

  private getAssetLink(asset: any): string {
    return asset.digitalAssetId ? `/digital-asset/${asset.digitalAssetId}/details` : null;
  }

  private isPAG(asset: any): boolean {
    return isPAG(asset);
  }

  private isThirdPartyAsset(asset: any): boolean {
    return isThirdPartyAsset(asset);
  }
}
